import {
  Children,
  createContext,
  forwardRef,
  isValidElement,
  useContext,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type DragEvent,
  type ForwardedRef,
  type KeyboardEvent as ReactKeyboardEvent,
  type MutableRefObject,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
  type UIEvent as ReactUIEvent,
} from "react";

import {
  useMonkeysBaseAppearance,
  useMonkeysComponentAttributes,
  useMonkeysLocaleMessages,
} from "../../provider/context";
import { resolveBaseAppearance } from "./theme";
import type {
  BaseAppearance,
  BaseResolvedTheme,
  BaseTableAlign,
  BaseTableBodyProps,
  BaseTableCaptionProps,
  BaseTableCellProps,
  BaseTableColumnDefinition,
  BaseTableColumnInteractionOptions,
  BaseTableContainerProps,
  BaseTableEmptyProps,
  BaseTableFooterProps,
  BaseTableHeaderProps,
  BaseTableHeadProps,
  BaseTableLayout,
  BaseTableLoadingProps,
  BaseTableProps,
  BaseTableRowProps,
  BaseTableSortDirection,
  BaseTableVerticalAlign,
} from './types';
import { cn, hasRenderableNode } from './utils';
import { SkeletonScope, SkeletonItem, useSkeletonMotion } from '../skeleton/motion';

const DATA_TRANSFER_COLUMN_TYPE = "application/x-monkeys-base-table-column";
const COLUMN_RESIZE_KEYBOARD_STEP = 8;

function assignRef<T>(ref: ForwardedRef<T> | undefined, value: T | null) {
  if (typeof ref === "function") {
    ref(value);
  } else if (ref) {
    ref.current = value;
  }
}

function assertBoolean(name: string, value: unknown) {
  if (value !== undefined && typeof value !== "boolean") {
    throw new Error(`BaseTable ${name} must be a boolean.`);
  }
}

function assertEnum<T extends string>(
  name: string,
  value: unknown,
  values: readonly T[],
) {
  if (value !== undefined && !values.includes(value as T)) {
    throw new Error(
      `BaseTable ${name} must be one of ${values.map((item) => `"${item}"`).join(", ")}.`,
    );
  }
}

function assertOptionalLabel(name: string, value: unknown) {
  if (
    value !== undefined &&
    (typeof value !== "string" || value.trim().length === 0)
  ) {
    throw new Error(
      `BaseTable ${name} must be a non-empty string when provided.`,
    );
  }
}

function assertOptionalFunction(name: string, value: unknown) {
  if (value !== undefined && typeof value !== "function") {
    throw new Error(`BaseTable ${name} must be a function when provided.`);
  }
}

function validateCssSizeValue(
  value: unknown,
  name: string,
  options: { stringAllowed?: boolean } = {},
) {
  if (value === undefined) return;
  if (typeof value === "number") {
    if (!Number.isFinite(value) || value < 0) {
      throw new Error(
        `BaseTable ${name} must be a finite non-negative number${
          options.stringAllowed ? " or a non-empty CSS size string" : ""
        }.`,
      );
    }
    return;
  }
  if (
    options.stringAllowed &&
    typeof value === "string" &&
    value.trim().length > 0
  ) {
    return;
  }
  throw new Error(
    `BaseTable ${name} must be ${
      options.stringAllowed
        ? "a finite non-negative number or a non-empty CSS size string"
        : "a finite non-negative number"
    }.`,
  );
}

function validateColumns(columns: unknown) {
  if (columns === undefined) return undefined;
  if (!Array.isArray(columns)) {
    throw new Error("BaseTable columns must be an array when provided.");
  }

  const ids = new Set<string>();
  columns.forEach((column, index) => {
    if (!column || typeof column !== "object" || Array.isArray(column)) {
      throw new Error(`BaseTable column at index ${index} must be an object.`);
    }
    const definition = column as BaseTableColumnDefinition;
    if (
      typeof definition.id !== "string" ||
      definition.id.trim().length === 0
    ) {
      throw new Error(
        `BaseTable column id at index ${index} must be a non-empty string.`,
      );
    }
    const columnId = definition.id.trim();
    if (definition.id !== columnId) {
      throw new Error(
        `BaseTable column id "${definition.id}" must not contain surrounding whitespace.`,
      );
    }
    if (ids.has(columnId)) {
      throw new Error(`BaseTable columns contain duplicate id "${columnId}".`);
    }
    ids.add(columnId);
    validateCssSizeValue(definition.width, `column "${columnId}" width`, {
      stringAllowed: true,
    });
    validateCssSizeValue(definition.minWidth, `column "${columnId}" minWidth`, {
      stringAllowed: true,
    });
    validateCssSizeValue(definition.maxWidth, `column "${columnId}" maxWidth`, {
      stringAllowed: true,
    });
    if (
      typeof definition.minWidth === "number" &&
      typeof definition.maxWidth === "number" &&
      definition.minWidth > definition.maxWidth
    ) {
      throw new Error(
        `BaseTable column "${columnId}" minWidth cannot be greater than maxWidth.`,
      );
    }
  });
  return ids;
}

function validateColumnOrder(
  columnOrder: unknown,
  knownColumnIds?: Set<string>,
) {
  if (columnOrder === undefined) return;
  if (!Array.isArray(columnOrder)) {
    throw new Error("BaseTable columnOrder must be an array when provided.");
  }

  const ids = new Set<string>();
  columnOrder.forEach((value, index) => {
    if (typeof value !== "string" || value.trim().length === 0) {
      throw new Error(
        `BaseTable columnOrder entry at index ${index} must be a non-empty string.`,
      );
    }
    const columnId = value.trim();
    if (value !== columnId) {
      throw new Error(
        `BaseTable columnOrder entry "${value}" must not contain surrounding whitespace.`,
      );
    }
    if (ids.has(columnId)) {
      throw new Error(
        `BaseTable columnOrder contains duplicate id "${columnId}".`,
      );
    }
    if (knownColumnIds && !knownColumnIds.has(columnId)) {
      throw new Error(
        `BaseTable columnOrder contains unknown column id "${columnId}".`,
      );
    }
    ids.add(columnId);
  });

  if (knownColumnIds && ids.size !== knownColumnIds.size) {
    const missing = [...knownColumnIds].filter(
      (columnId) => !ids.has(columnId),
    );
    throw new Error(
      `BaseTable columnOrder must contain every configured column id; missing ${missing
        .map((columnId) => `"${columnId}"`)
        .join(", ")}.`,
    );
  }
}

function validateColumnSizes(
  columnSizes: unknown,
  knownColumnIds?: Set<string>,
) {
  if (columnSizes === undefined) return;
  if (
    !columnSizes ||
    typeof columnSizes !== "object" ||
    Array.isArray(columnSizes) ||
    ![Object.prototype, null].includes(Object.getPrototypeOf(columnSizes))
  ) {
    throw new Error(
      "BaseTable columnSizes must be a plain record when provided.",
    );
  }

  Object.entries(columnSizes).forEach(([columnId, value]) => {
    const normalizedColumnId = columnId.trim();
    if (normalizedColumnId.length === 0) {
      throw new Error("BaseTable columnSizes keys must be non-empty strings.");
    }
    if (columnId !== normalizedColumnId) {
      throw new Error(
        `BaseTable columnSizes key "${columnId}" must not contain surrounding whitespace.`,
      );
    }
    if (knownColumnIds && !knownColumnIds.has(normalizedColumnId)) {
      throw new Error(
        `BaseTable columnSizes contains unknown column id "${columnId}".`,
      );
    }
    validateCssSizeValue(value, `columnSizes["${columnId}"]`);
  });
}

function isRtl(element: HTMLElement) {
  const ownerWindow = element.ownerDocument.defaultView;
  if (ownerWindow) {
    return ownerWindow.getComputedStyle(element).direction === "rtl";
  }
  return element.closest('[dir="rtl"]') !== null;
}

function setPointerCaptureIfAvailable(target: HTMLElement, pointerId: number) {
  if (!target.setPointerCapture) return;
  try {
    target.setPointerCapture(pointerId);
  } catch (error) {
    const OwnerDOMException = target.ownerDocument.defaultView?.DOMException;
    if (
      OwnerDOMException &&
      error instanceof OwnerDOMException &&
      error.name === "NotFoundError"
    ) {
      return;
    }
    throw error;
  }
}

type BaseTableContextValue = BaseTableColumnInteractionOptions & {
  appearance?: BaseAppearance;
  theme: BaseResolvedTheme;
  layout: BaseTableLayout;
  columnTemplate?: string;
  bodyMaxHeight?: number | string;
  bodyClassName?: string;
  splitHeaderRef?: MutableRefObject<HTMLTableSectionElement | null>;
  splitFooterRef?: MutableRefObject<HTMLTableSectionElement | null>;
  draggingColumnId?: string;
  setDraggingColumnId: (columnId?: string) => void;
};

const BaseTableContext = createContext<BaseTableContextValue | null>(null);

function useBaseTableContext(appearance?: BaseAppearance) {
  const context = useContext(BaseTableContext);
  const providerAppearance = useMonkeysBaseAppearance(appearance);
  if (context) {
    const resolvedAppearance =
      appearance === undefined ? context.appearance : providerAppearance;
    return {
      ...context,
      theme:
        appearance === undefined
          ? context.theme
          : resolveBaseAppearance(resolvedAppearance),
      appearance: resolvedAppearance,
    };
  }

  const theme = resolveBaseAppearance(providerAppearance);
  return {
    appearance: providerAppearance,
    theme,
    layout: "native",
    setDraggingColumnId: () => {},
  } satisfies BaseTableContextValue;
}

function hasSplitScrollTable(children: ReactNode): boolean {
  let found = false;

  Children.forEach(children, (child) => {
    if (found || !isValidElement(child)) return;

    const childProps = child.props as {
      children?: ReactNode;
      layout?: unknown;
    };

    if (childProps.layout === "split-scroll") {
      found = true;
      return;
    }

    if (childProps.children) {
      found = hasSplitScrollTable(childProps.children);
    }
  });

  return found;
}

function toCssSize(value?: number | string) {
  if (value === undefined) return undefined;
  return typeof value === "number" ? `${value}px` : value;
}

function validateTableCssSize(
  value: number | string | undefined,
  property: string,
) {
  if (value === undefined) return undefined;
  if (typeof value === "number") {
    if (!Number.isFinite(value) || value < 0) {
      throw new Error(
        `BaseTable ${property} must be a finite non-negative number when numeric.`,
      );
    }
    return `${value}px`;
  }

  if (value.trim().length === 0) {
    throw new Error(`BaseTable ${property} must not be an empty CSS size.`);
  }

  return value;
}

function validateCssSize(
  value: number | string | undefined,
  columnId: string,
  property: string,
) {
  if (value === undefined) return undefined;
  if (typeof value === "number") {
    if (!Number.isFinite(value) || value < 0) {
      throw new Error(
        `BaseTable split-scroll column "${columnId}" has invalid ${property}. Expected a finite non-negative number.`,
      );
    }
    return `${value}px`;
  }

  if (value.trim().length === 0) {
    throw new Error(
      `BaseTable split-scroll column "${columnId}" has an empty ${property} value.`,
    );
  }

  return value;
}

function validateNumericSize(
  value: number | string | undefined,
  columnId: string,
  property: string,
) {
  if (value === undefined || typeof value !== "number") return undefined;
  if (!Number.isFinite(value) || value < 0) {
    throw new Error(
      `BaseTable split-scroll column "${columnId}" has invalid ${property}. Expected a finite non-negative number.`,
    );
  }
  return value;
}

function buildSplitColumnTemplate(
  columns: BaseTableColumnDefinition[] | undefined,
  columnSizes: BaseTableColumnInteractionOptions["columnSizes"],
) {
  if (!columns?.length) {
    throw new Error(
      'BaseTable layout="split-scroll" requires at least one column definition.',
    );
  }

  const seenColumnIds = new Set<string>();
  const tracks = columns.map((column, index) => {
    const columnId = column.id.trim();
    if (!columnId) {
      throw new Error(
        `BaseTable split-scroll column at index ${index} requires a non-empty id.`,
      );
    }
    if (seenColumnIds.has(columnId)) {
      throw new Error(
        `BaseTable layout="split-scroll" received duplicate column id "${columnId}".`,
      );
    }
    seenColumnIds.add(columnId);

    const columnSize = columnSizes?.[columnId];
    const numericMinWidth = validateNumericSize(
      column.minWidth,
      columnId,
      "minWidth",
    );
    const numericMaxWidth = validateNumericSize(
      column.maxWidth,
      columnId,
      "maxWidth",
    );
    if (
      numericMinWidth !== undefined &&
      numericMaxWidth !== undefined &&
      numericMaxWidth < numericMinWidth
    ) {
      throw new Error(
        `BaseTable split-scroll column "${columnId}" has maxWidth smaller than minWidth.`,
      );
    }

    const minWidth = validateCssSize(column.minWidth, columnId, "minWidth");
    const maxWidth = validateCssSize(column.maxWidth, columnId, "maxWidth");
    const baseWidth =
      columnSize !== undefined
        ? clampWidth(columnSize, numericMinWidth, numericMaxWidth)
        : column.width;
    const width = validateCssSize(baseWidth, columnId, "width");
    if (width) return width;

    return `minmax(${minWidth ?? "0px"}, ${maxWidth ?? "1fr"})`;
  });

  return `${tracks.join(" ")} var(--base-table-split-scrollbar-gutter)`;
}

function clampWidth(value: number, minWidth?: number, maxWidth?: number) {
  const min = minWidth ?? 48;
  const max = maxWidth ?? Number.POSITIVE_INFINITY;
  return Math.min(Math.max(value, min), max);
}

function getNextColumnOrder(
  order: string[] | undefined,
  columnId: string,
  targetColumnId: string,
) {
  if (!order || columnId === targetColumnId) return undefined;
  const fromIndex = order.indexOf(columnId);
  const toIndex = order.indexOf(targetColumnId);
  if (fromIndex < 0 || toIndex < 0) return undefined;

  const next = [...order];
  const [moved] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, moved);
  return next;
}

function sortAriaValue(direction?: BaseTableSortDirection) {
  switch (direction) {
    case "asc":
      return "ascending";
    case "desc":
      return "descending";
    default:
      return undefined;
  }
}

function getSplitHeadJustifyContent(align: BaseTableAlign) {
  switch (align) {
    case "center":
      return "center";
    case "right":
      return "flex-end";
    default:
      return "flex-start";
  }
}

function getSplitHeadAlignItems(verticalAlign: BaseTableVerticalAlign) {
  switch (verticalAlign) {
    case "top":
      return "flex-start";
    case "bottom":
      return "flex-end";
    default:
      return "center";
  }
}

function SortIndicator({
  direction,
  className,
}: {
  direction?: BaseTableSortDirection;
  className?: string;
}) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        className,
        direction === "desc" && "rotate-180",
        (!direction || direction === "none") && "opacity-45",
      )}
    >
      ▲
    </span>
  );
}

function DragHandleIcon() {
  return (
    <span aria-hidden="true" className="grid gap-0.5">
      <span className="h-0.5 w-0.5 rounded-full bg-current" />
      <span className="h-0.5 w-0.5 rounded-full bg-current" />
      <span className="h-0.5 w-0.5 rounded-full bg-current" />
    </span>
  );
}

export const BaseTableContainer = forwardRef<
  HTMLDivElement,
  BaseTableContainerProps
>(function BaseTableContainer(
  {
    appearance,
    attachedFooter = false,
    children,
    className,
    classNames,
    style,
    ...containerProps
  },
  ref,
) {
  assertBoolean("attachedFooter", attachedFooter);
  const resolvedAppearance = useMonkeysBaseAppearance(appearance);
  const componentAttributes = useMonkeysComponentAttributes();
  const theme = resolveBaseAppearance(resolvedAppearance);
  const containsSplitScrollTable = hasSplitScrollTable(children);
  const splitScrollContainerStyle = containsSplitScrollTable
    ? ({
        overflow: "hidden",
        scrollbarGutter: "auto",
      } as CSSProperties)
    : undefined;

  return (
    <div
      {...componentAttributes}
      {...containerProps}
      ref={ref}
      data-monkeys-component="table-container"
      data-slot="container"
      data-base-table-container=""
      data-attached-footer={attachedFooter ? "" : undefined}
      className={cn(
        theme.slots.tableContainer,
        attachedFooter && theme.slots.tableContainerFooterAttached,
        classNames?.root,
        className,
      )}
      style={{
        ...theme.styles.tableContainer,
        ...(attachedFooter
          ? theme.styles.tableContainerFooterAttached
          : undefined),
        ...splitScrollContainerStyle,
        ...style,
      }}
    >
      {children}
    </div>
  );
});

export const BaseTable = forwardRef<HTMLTableElement, BaseTableProps>(
  function BaseTable(
    {
      appearance,
      className,
      classNames,
      style,
      layout = "native",
      columns,
      bodyMaxHeight,
      bodyClassName,
      columnOrder,
      columnSizes,
      columnDragEnabled = false,
      columnResizeEnabled = false,
      onColumnOrderChange,
      onColumnSizeChange,
      ...tableProps
    },
    ref,
  ) {
    assertEnum("layout", layout, ["native", "split-scroll"] as const);
    assertBoolean("columnDragEnabled", columnDragEnabled);
    assertBoolean("columnResizeEnabled", columnResizeEnabled);
    assertOptionalFunction("onColumnOrderChange", onColumnOrderChange);
    assertOptionalFunction("onColumnSizeChange", onColumnSizeChange);
    validateCssSizeValue(bodyMaxHeight, "bodyMaxHeight", {
      stringAllowed: true,
    });
    const knownColumnIds = validateColumns(columns);
    validateColumnOrder(columnOrder, knownColumnIds);
    validateColumnSizes(columnSizes, knownColumnIds);
    const resolvedAppearance = useMonkeysBaseAppearance(appearance);
    const componentAttributes = useMonkeysComponentAttributes();
    const theme = resolveBaseAppearance(resolvedAppearance);
    const [draggingColumnId, setDraggingColumnId] = useState<
      string | undefined
    >();
    const splitHeaderRef = useRef<HTMLTableSectionElement | null>(null);
    const splitFooterRef = useRef<HTMLTableSectionElement | null>(null);
    const isSplitScroll = layout === "split-scroll";
    const columnTemplate = isSplitScroll
      ? buildSplitColumnTemplate(columns, columnSizes)
      : undefined;
    const contextValue: BaseTableContextValue = {
      appearance: resolvedAppearance,
      theme,
      layout,
      columnTemplate,
      bodyMaxHeight,
      bodyClassName,
      splitHeaderRef,
      splitFooterRef,
      columnOrder,
      columnSizes,
      columnDragEnabled,
      columnResizeEnabled,
      onColumnOrderChange,
      onColumnSizeChange,
      draggingColumnId,
      setDraggingColumnId,
    };

    return (
      <BaseTableContext.Provider value={contextValue}>
        <table
          {...componentAttributes}
          {...tableProps}
          ref={ref}
          data-monkeys-component="table"
          data-slot="root"
          data-base-table-layout={layout}
          className={cn(
            theme.slots.tableRoot,
            isSplitScroll && theme.slots.tableSplitRoot,
            classNames?.root,
            className,
          )}
          style={{
            ...theme.styles.table,
            ...(isSplitScroll ? theme.styles.tableSplitRoot : undefined),
            ...(columnTemplate
              ? ({
                  "--base-table-column-template": columnTemplate,
                } as CSSProperties)
              : undefined),
            ...style,
          }}
        />
      </BaseTableContext.Provider>
    );
  },
);

export const BaseTableHeader = forwardRef<
  HTMLTableSectionElement,
  BaseTableHeaderProps
>(function BaseTableHeader(
  { appearance, className, classNames, style, ...headerProps },
  ref,
) {
  const { layout, splitHeaderRef, theme } = useBaseTableContext(appearance);
  const isSplitScroll = layout === "split-scroll";
  const setHeaderRef = (node: HTMLTableSectionElement | null) => {
    if (splitHeaderRef) splitHeaderRef.current = isSplitScroll ? node : null;
    assignRef(ref, node);
  };

  return (
    <thead
      {...headerProps}
      ref={setHeaderRef}
      data-slot="header"
      className={cn(
        theme.slots.tableHeader,
        isSplitScroll && theme.slots.tableSplitHeader,
        classNames?.root,
        className,
      )}
      style={{
        ...theme.styles.tableHeader,
        ...(isSplitScroll ? theme.styles.tableSplitHeader : undefined),
        ...style,
      }}
    />
  );
});

export const BaseTableBody = forwardRef<
  HTMLTableSectionElement,
  BaseTableBodyProps
>(function BaseTableBody(
  { appearance, className, classNames, onScroll, style, ...bodyProps },
  ref,
) {
  const {
    bodyClassName,
    bodyMaxHeight,
    layout,
    splitFooterRef,
    splitHeaderRef,
    theme,
  } = useBaseTableContext(appearance);
  const isSplitScroll = layout === "split-scroll";
  const handleScroll = isSplitScroll
    ? (event: ReactUIEvent<HTMLTableSectionElement>) => {
        const scrollLeft = event.currentTarget.scrollLeft;

        if (splitHeaderRef?.current) {
          splitHeaderRef.current.scrollLeft = scrollLeft;
        }
        if (splitFooterRef?.current) {
          splitFooterRef.current.scrollLeft = scrollLeft;
        }
        onScroll?.(event);
      }
    : onScroll;

  return (
    <tbody
      {...bodyProps}
      ref={ref}
      data-slot="body"
      onScroll={handleScroll}
      className={cn(
        theme.slots.tableBody,
        isSplitScroll && theme.slots.tableSplitBody,
        isSplitScroll && bodyClassName,
        classNames?.root,
        className,
      )}
      style={{
        ...theme.styles.tableBody,
        ...(isSplitScroll ? theme.styles.tableSplitBody : undefined),
        ...(isSplitScroll && bodyMaxHeight !== undefined
          ? { maxHeight: validateTableCssSize(bodyMaxHeight, "bodyMaxHeight") }
          : undefined),
        ...style,
      }}
    />
  );
});

export const BaseTableFooter = forwardRef<
  HTMLTableSectionElement,
  BaseTableFooterProps
>(function BaseTableFooter(
  { appearance, className, classNames, style, ...footerProps },
  ref,
) {
  const { layout, splitFooterRef, theme } = useBaseTableContext(appearance);
  const isSplitScroll = layout === "split-scroll";
  const setFooterRef = (node: HTMLTableSectionElement | null) => {
    if (splitFooterRef) splitFooterRef.current = isSplitScroll ? node : null;
    assignRef(ref, node);
  };

  return (
    <tfoot
      {...footerProps}
      ref={setFooterRef}
      data-slot="footer"
      className={cn(
        theme.slots.tableFooter,
        isSplitScroll && theme.slots.tableSplitFooter,
        classNames?.root,
        className,
      )}
      style={{
        ...theme.styles.tableFooter,
        ...(isSplitScroll ? theme.styles.tableSplitFooter : undefined),
        ...style,
      }}
    />
  );
});

export const BaseTableRow = forwardRef<HTMLTableRowElement, BaseTableRowProps>(
  function BaseTableRow(
    {
      interactive,
      selected,
      disabled,
      appearance,
      className,
      classNames,
      style,
      ...rowProps
    },
    ref,
  ) {
    assertBoolean("interactive", interactive);
    assertBoolean("selected", selected);
    assertBoolean("disabled", disabled);
    const { layout, theme } = useBaseTableContext(appearance);
    const isSplitScroll = layout === "split-scroll";

    return (
      <tr
        {...rowProps}
        ref={ref}
        data-slot="row"
        aria-disabled={disabled || undefined}
        data-state={selected ? "selected" : undefined}
        className={cn(
          theme.slots.tableRow,
          isSplitScroll && theme.slots.tableSplitRow,
          interactive && !disabled && theme.slots.tableRowInteractive,
          selected && theme.slots.tableRowSelected,
          disabled && theme.slots.tableRowDisabled,
          classNames?.root,
          className,
        )}
        style={{
          ...theme.styles.tableRow,
          ...(isSplitScroll ? theme.styles.tableSplitRow : undefined),
          ...style,
        }}
      />
    );
  },
);

export const BaseTableHead = forwardRef<
  HTMLTableCellElement,
  BaseTableHeadProps
>(function BaseTableHead(
  {
    presentation,
    columnId,
    align,
    verticalAlign,
    icon,
    label,
    description,
    meta,
    actions,
    trailing,
    sortable,
    sortDirection = "none",
    sortLabel,
    onSort,
    draggable,
    dragLabel,
    resizable,
    resizeLabel,
    width,
    minWidth,
    maxWidth,
    appearance,
    className,
    classNames,
    style,
    children,
    onDragOver,
    onDrop,
    ...headProps
  },
  forwardedRef,
) {
  assertEnum("head presentation", presentation, ["classic-data-grid"] as const);
  assertEnum("head align", align, ["left", "center", "right"] as const);
  assertEnum("head verticalAlign", verticalAlign, [
    "top",
    "middle",
    "bottom",
  ] as const);
  assertEnum("head sortDirection", sortDirection, [
    "asc",
    "desc",
    "none",
  ] as const);
  assertBoolean("head sortable", sortable);
  assertBoolean("head draggable", draggable);
  assertBoolean("head resizable", resizable);
  assertOptionalFunction("head onSort", onSort);
  assertOptionalLabel("head sortLabel", sortLabel);
  assertOptionalLabel("head dragLabel", dragLabel);
  assertOptionalLabel("head resizeLabel", resizeLabel);
  if (
    columnId !== undefined &&
    (typeof columnId !== "string" || columnId.trim().length === 0)
  ) {
    throw new Error(
      "BaseTable head columnId must be a non-empty string when provided.",
    );
  }
  validateCssSizeValue(width, "head width", { stringAllowed: true });
  validateCssSizeValue(minWidth, "head minWidth");
  validateCssSizeValue(maxWidth, "head maxWidth");
  if (minWidth !== undefined && maxWidth !== undefined && minWidth > maxWidth) {
    throw new Error("BaseTable head minWidth cannot be greater than maxWidth.");
  }
  const tableContext = useBaseTableContext(appearance);
  const { theme } = tableContext;
  const messages = useMonkeysLocaleMessages().table;
  const isSplitScroll = tableContext.layout === "split-scroll";
  const classicDataGrid = presentation === "classic-data-grid";
  const resolvedAlign = align ?? (isSplitScroll ? "center" : "left");
  const resolvedVerticalAlign = verticalAlign ?? "middle";
  const headRef = useRef<HTMLTableCellElement | null>(null);
  const resizeCleanupRef = useRef<() => void>(() => {});
  const [measuredWidth, setMeasuredWidth] = useState<number>();
  const setHeadRef = (node: HTMLTableCellElement | null) => {
    headRef.current = node;
    assignRef(forwardedRef, node);
  };
  const controlledWidth =
    columnId && tableContext.columnSizes?.[columnId] !== undefined
      ? tableContext.columnSizes[columnId]
      : width;
  const canDrag = Boolean(
    columnId &&
      draggable !== false &&
      (draggable || tableContext.columnDragEnabled) &&
      tableContext.columnOrder &&
      tableContext.onColumnOrderChange,
  );
  const canResize = Boolean(
    columnId &&
      resizable !== false &&
      (resizable || tableContext.columnResizeEnabled) &&
      tableContext.onColumnSizeChange,
  );
  if (canResize && maxWidth !== undefined && maxWidth < (minWidth ?? 48)) {
    throw new Error(
      "BaseTable resizable head maxWidth cannot be smaller than its effective minWidth.",
    );
  }
  const interactiveSort = Boolean(sortable || onSort);
  const ariaSort = sortAriaValue(sortDirection);

  const content = hasRenderableNode(label) ? label : children;
  const hasVisibleSortName =
    hasRenderableNode(content) || hasRenderableNode(description);
  const sortAccessibleName =
    sortLabel ??
    (!hasVisibleSortName
      ? messages.sortColumn({ columnId: columnId?.trim() || undefined })
      : undefined);
  const usesRichHeader =
    canDrag ||
    hasRenderableNode(icon) ||
    hasRenderableNode(label) ||
    hasRenderableNode(description) ||
    hasRenderableNode(meta) ||
    hasRenderableNode(actions) ||
    hasRenderableNode(trailing) ||
    interactiveSort;
  const renderedContent = classicDataGrid ? (
    <div
      className={cn(
        "flex h-full min-w-0 select-none items-center",
        resolvedAlign === "center"
          ? "justify-center"
          : resolvedAlign === "right"
            ? "justify-end"
            : "justify-start",
        canDrag && "cursor-grab active:cursor-grabbing",
      )}
      {...(canDrag
        ? {
            draggable: true,
            role: "button",
            tabIndex: 0,
            "aria-disabled": false,
            "aria-roledescription": "sortable",
            onKeyDown: (event: ReactKeyboardEvent<HTMLDivElement>) => {
              if (
                !columnId ||
                !tableContext.columnOrder ||
                (event.key !== "ArrowLeft" && event.key !== "ArrowRight")
              ) {
                return;
              }
              const currentIndex = tableContext.columnOrder.indexOf(columnId);
              if (currentIndex < 0) return;
              const rtl = isRtl(event.currentTarget);
              const physicalDelta = event.key === "ArrowLeft" ? -1 : 1;
              const nextIndex =
                currentIndex + (rtl ? -physicalDelta : physicalDelta);
              const targetColumnId = tableContext.columnOrder[nextIndex];
              if (!targetColumnId) return;
              const nextOrder = getNextColumnOrder(
                tableContext.columnOrder,
                columnId,
                targetColumnId,
              );
              if (!nextOrder) return;
              event.preventDefault();
              tableContext.onColumnOrderChange?.(nextOrder, {
                columnId,
                targetColumnId,
              });
            },
            onDragStart: (event: DragEvent<HTMLDivElement>) => {
              event.stopPropagation();
              tableContext.setDraggingColumnId(columnId as string);
              event.dataTransfer.effectAllowed = "move";
              event.dataTransfer.setData(
                DATA_TRANSFER_COLUMN_TYPE,
                columnId as string,
              );
            },
            onDragEnd: (event: DragEvent<HTMLDivElement>) => {
              event.stopPropagation();
              tableContext.setDraggingColumnId(undefined);
            },
          }
        : {})}
    >
      {content}
    </div>
  ) : usesRichHeader ? (
    <div className={cn(theme.slots.tableHeadContent, classNames?.content)}>
      <div className={cn(theme.slots.tableHeadMain, classNames?.main)}>
        {canDrag ? (
          <button
            type="button"
            draggable
            title={dragLabel}
            aria-label={
              dragLabel ?? messages.dragColumn({ columnId: columnId as string })
            }
            className={cn(
              theme.slots.tableHeadDragHandle,
              classNames?.dragHandle,
            )}
            onClick={(event) => event.stopPropagation()}
            onKeyDown={(event) => {
              if (
                !columnId ||
                !tableContext.columnOrder ||
                (event.key !== "ArrowLeft" && event.key !== "ArrowRight")
              ) {
                return;
              }
              const currentIndex = tableContext.columnOrder.indexOf(columnId);
              if (currentIndex < 0) return;
              const rtl = isRtl(event.currentTarget);
              const physicalDelta = event.key === "ArrowLeft" ? -1 : 1;
              const nextIndex =
                currentIndex + (rtl ? -physicalDelta : physicalDelta);
              const targetColumnId = tableContext.columnOrder[nextIndex];
              if (!targetColumnId) return;
              const nextOrder = getNextColumnOrder(
                tableContext.columnOrder,
                columnId,
                targetColumnId,
              );
              if (!nextOrder) return;
              event.preventDefault();
              tableContext.onColumnOrderChange?.(nextOrder, {
                columnId,
                targetColumnId,
              });
            }}
            onDragStart={(event) => {
              event.stopPropagation();
              tableContext.setDraggingColumnId(columnId);
              event.dataTransfer.effectAllowed = "move";
              event.dataTransfer.setData(
                DATA_TRANSFER_COLUMN_TYPE,
                columnId as string,
              );
            }}
            onDragEnd={(event) => {
              event.stopPropagation();
              tableContext.setDraggingColumnId(undefined);
            }}
          >
            <DragHandleIcon />
          </button>
        ) : null}
        {icon ? (
          <span className={cn(theme.slots.tableHeadIcon, classNames?.icon)}>
            {icon}
          </span>
        ) : null}
        {interactiveSort ? (
          <button
            type="button"
            disabled={!onSort}
            title={sortLabel}
            aria-label={sortAccessibleName}
            className={cn(
              theme.slots.tableHeadSortButton,
              classNames?.sortButton,
            )}
            onClick={(event) => {
              event.stopPropagation();
              onSort?.(columnId);
            }}
          >
            <span className={cn(theme.slots.tableHeadText, classNames?.text)}>
              {hasRenderableNode(content) ? (
                <span
                  className={cn(theme.slots.tableHeadLabel, classNames?.label)}
                >
                  {content}
                </span>
              ) : null}
              {hasRenderableNode(description) ? (
                <span
                  className={cn(
                    theme.slots.tableHeadDescription,
                    classNames?.description,
                  )}
                >
                  {description}
                </span>
              ) : null}
            </span>
            <SortIndicator
              direction={sortDirection}
              className={cn(
                theme.slots.tableHeadSortIcon,
                classNames?.sortIcon,
              )}
            />
          </button>
        ) : (
          <span className={cn(theme.slots.tableHeadText, classNames?.text)}>
            {hasRenderableNode(content) ? (
              <span
                className={cn(theme.slots.tableHeadLabel, classNames?.label)}
              >
                {content}
              </span>
            ) : null}
            {hasRenderableNode(description) ? (
              <span
                className={cn(
                  theme.slots.tableHeadDescription,
                  classNames?.description,
                )}
              >
                {description}
              </span>
            ) : null}
          </span>
        )}
      </div>
      {hasRenderableNode(meta) ||
      hasRenderableNode(actions) ||
      hasRenderableNode(trailing) ? (
        <div className={cn(theme.slots.tableHeadActions, classNames?.actions)}>
          {hasRenderableNode(meta) ? (
            <span className={cn(theme.slots.tableHeadMeta, classNames?.meta)}>
              {meta}
            </span>
          ) : null}
          {actions}
          {trailing}
        </div>
      ) : null}
    </div>
  ) : (
    children
  );

  const handleDragOver = (event: DragEvent<HTMLTableCellElement>) => {
    onDragOver?.(event);
    if (
      !canDrag ||
      !tableContext.draggingColumnId ||
      tableContext.draggingColumnId === columnId
    ) {
      return;
    }
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (event: DragEvent<HTMLTableCellElement>) => {
    onDrop?.(event);
    if (!canDrag || !columnId) return;
    const sourceColumnId =
      tableContext.draggingColumnId ||
      event.dataTransfer.getData(DATA_TRANSFER_COLUMN_TYPE);
    if (!sourceColumnId || sourceColumnId === columnId) return;

    const nextOrder = getNextColumnOrder(
      tableContext.columnOrder,
      sourceColumnId,
      columnId,
    );
    if (nextOrder) {
      event.preventDefault();
      tableContext.onColumnOrderChange?.(nextOrder, {
        columnId: sourceColumnId,
        targetColumnId: columnId,
      });
    }
    tableContext.setDraggingColumnId(undefined);
  };

  const handleResizePointerDown = (event: ReactPointerEvent<HTMLElement>) => {
    if (
      !canResize ||
      !columnId ||
      !tableContext.onColumnSizeChange ||
      (event.button !== 0 && event.pointerType !== "touch")
    ) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    resizeCleanupRef.current();

    const startX = event.clientX;
    const currentWidth =
      currentResizeWidth ?? headRef.current?.offsetWidth ?? minWidth ?? 48;
    const previousWidth = clampWidth(currentWidth, minWidth, maxWidth);
    const pointerId = event.pointerId;
    const target = event.currentTarget;
    const ownerDocument = target.ownerDocument;
    const rtl = isRtl(target);

    const handlePointerMove = (moveEvent: PointerEvent) => {
      if (moveEvent.pointerId !== pointerId) return;
      const nextWidth = clampWidth(
        previousWidth +
          (rtl ? startX - moveEvent.clientX : moveEvent.clientX - startX),
        minWidth,
        maxWidth,
      );
      tableContext.onColumnSizeChange?.(columnId, nextWidth, {
        columnId,
        previousWidth,
        minWidth,
        maxWidth,
      });
    };

    const handleStop = (nextEvent: PointerEvent) => {
      if (nextEvent.pointerId !== pointerId) return;
      resizeCleanupRef.current();
    };
    const handleLostCapture = (nextEvent: PointerEvent) => {
      if (nextEvent.pointerId !== pointerId) return;
      resizeCleanupRef.current();
    };

    resizeCleanupRef.current = () => {
      ownerDocument.removeEventListener("pointermove", handlePointerMove);
      ownerDocument.removeEventListener("pointerup", handleStop);
      ownerDocument.removeEventListener("pointercancel", handleStop);
      target.removeEventListener("lostpointercapture", handleLostCapture);
      if (target.hasPointerCapture?.(pointerId)) {
        target.releasePointerCapture?.(pointerId);
      }
      resizeCleanupRef.current = () => {};
    };
    setPointerCaptureIfAvailable(target, pointerId);
    ownerDocument.addEventListener("pointermove", handlePointerMove);
    ownerDocument.addEventListener("pointerup", handleStop);
    ownerDocument.addEventListener("pointercancel", handleStop);
    target.addEventListener("lostpointercapture", handleLostCapture);
  };

  const currentResizeWidth =
    typeof controlledWidth === "number"
      ? clampWidth(controlledWidth, minWidth, maxWidth)
      : measuredWidth;
  const announcedResizeWidth =
    currentResizeWidth ?? clampWidth(minWidth ?? 48, minWidth, maxWidth);

  const handleResizeKeyDown = (
    event: ReactKeyboardEvent<HTMLButtonElement>,
  ) => {
    if (!canResize || !columnId || !tableContext.onColumnSizeChange) return;
    const rtl = isRtl(event.currentTarget);
    const previousWidth =
      currentResizeWidth ??
      clampWidth(
        headRef.current?.offsetWidth || minWidth || 48,
        minWidth,
        maxWidth,
      );
    let nextWidth: number | undefined;
    if (event.key === "ArrowLeft") {
      nextWidth =
        previousWidth +
        (rtl ? COLUMN_RESIZE_KEYBOARD_STEP : -COLUMN_RESIZE_KEYBOARD_STEP);
    }
    if (event.key === "ArrowRight") {
      nextWidth =
        previousWidth +
        (rtl ? -COLUMN_RESIZE_KEYBOARD_STEP : COLUMN_RESIZE_KEYBOARD_STEP);
    }
    if (event.key === "Home" && minWidth !== undefined) nextWidth = minWidth;
    if (event.key === "End" && maxWidth !== undefined) nextWidth = maxWidth;
    if (nextWidth === undefined) return;
    event.preventDefault();
    event.stopPropagation();
    tableContext.onColumnSizeChange(
      columnId,
      clampWidth(nextWidth, minWidth, maxWidth),
      {
        columnId,
        previousWidth,
        minWidth,
        maxWidth,
      },
    );
  };

  useEffect(() => {
    if (typeof controlledWidth === "number") {
      setMeasuredWidth(undefined);
      return;
    }
    const measured = headRef.current?.getBoundingClientRect().width;
    if (measured && Number.isFinite(measured)) {
      const nextWidth = clampWidth(measured, minWidth, maxWidth);
      setMeasuredWidth((current) =>
        current === nextWidth ? current : nextWidth,
      );
    }
  }, [controlledWidth, minWidth, maxWidth, canResize]);

  useEffect(() => {
    if (!canResize) resizeCleanupRef.current();
    return () => resizeCleanupRef.current();
  }, [canResize, columnId, tableContext.onColumnSizeChange]);

  return (
    <th
      {...headProps}
      ref={setHeadRef}
      data-slot="head"
      data-v2-header-drag-id={classicDataGrid && canDrag ? columnId : undefined}
      scope={headProps.scope ?? "col"}
      aria-sort={ariaSort}
      className={cn(
        theme.slots.tableHead,
        isSplitScroll && theme.slots.tableSplitHead,
        resolvedAlign === "center" && theme.slots.tableCellAlignCenter,
        resolvedAlign === "right" && theme.slots.tableCellAlignRight,
        classicDataGrid &&
          "!relative !h-9 !px-3 !align-middle !text-sm !font-medium !text-muted-foreground after:pointer-events-none after:absolute after:bottom-2 after:right-0 after:top-2 after:w-px after:bg-border/50 after:content-['']",
        classNames?.root,
        className,
      )}
      style={{
        ...theme.styles.tableHead,
        ...(isSplitScroll ? theme.styles.tableSplitHead : undefined),
        width: isSplitScroll ? undefined : toCssSize(controlledWidth),
        minWidth: isSplitScroll ? undefined : toCssSize(minWidth),
        maxWidth: isSplitScroll ? undefined : toCssSize(maxWidth),
        ...(isSplitScroll
          ? {
              alignItems: getSplitHeadAlignItems(resolvedVerticalAlign),
              justifyContent: getSplitHeadJustifyContent(resolvedAlign),
              textAlign: resolvedAlign,
            }
          : verticalAlign
            ? { verticalAlign }
            : undefined),
        ...style,
      }}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {renderedContent}
      {canResize && classicDataGrid ? (
        <div
          data-column-resize-handle="true"
          className="absolute right-0 top-0 z-20 h-full w-2 cursor-col-resize touch-none select-none hover:bg-muted"
          onPointerDown={handleResizePointerDown}
        />
      ) : canResize ? (
        <button
          type="button"
          role="separator"
          aria-orientation="vertical"
          aria-valuenow={announcedResizeWidth}
          aria-valuemin={minWidth ?? 48}
          aria-valuemax={maxWidth}
          aria-valuetext={`${announcedResizeWidth}px`}
          title={resizeLabel}
          aria-label={
            resizeLabel ??
            messages.resizeColumn({ columnId: columnId as string })
          }
          className={cn(
            theme.slots.tableHeadResizeHandle,
            classNames?.resizeHandle,
          )}
          onClick={(event) => event.stopPropagation()}
          onKeyDown={handleResizeKeyDown}
          onPointerDown={handleResizePointerDown}
        />
      ) : null}
    </th>
  );
});

export const BaseTableCell = forwardRef<
  HTMLTableCellElement,
  BaseTableCellProps
>(function BaseTableCell(
  { align = "left", appearance, className, classNames, style, ...cellProps },
  ref,
) {
  assertEnum("cell align", align, ["left", "center", "right"] as const);
  const { layout, theme } = useBaseTableContext(appearance);
  const isSplitScroll = layout === "split-scroll";

  return (
    <td
      {...cellProps}
      ref={ref}
      data-slot="cell"
      className={cn(
        theme.slots.tableCell,
        isSplitScroll && theme.slots.tableSplitCell,
        align === "center" && theme.slots.tableCellAlignCenter,
        align === "right" && theme.slots.tableCellAlignRight,
        classNames?.root,
        className,
      )}
      style={{
        ...theme.styles.tableCell,
        ...(isSplitScroll ? theme.styles.tableSplitCell : undefined),
        ...style,
      }}
    />
  );
});

export const BaseTableCaption = forwardRef<
  HTMLTableCaptionElement,
  BaseTableCaptionProps
>(function BaseTableCaption(
  { appearance, className, classNames, style, ...captionProps },
  ref,
) {
  const { theme } = useBaseTableContext(appearance);

  return (
    <caption
      {...captionProps}
      ref={ref}
      data-slot="caption"
      className={cn(theme.slots.tableCaption, classNames?.root, className)}
      style={{
        ...theme.styles.tableCaption,
        ...style,
      }}
    />
  );
});

export const BaseTableEmpty = forwardRef<
  HTMLTableRowElement,
  BaseTableEmptyProps
>(function BaseTableEmpty(
  {
    colSpan,
    icon,
    title,
    description,
    actions,
    children,
    appearance,
    className,
    classNames,
    style,
    ...emptyProps
  },
  ref,
) {
  if (!Number.isSafeInteger(colSpan) || colSpan < 1) {
    throw new Error("BaseTable empty colSpan must be a positive safe integer.");
  }
  const { layout, theme } = useBaseTableContext(appearance);
  const isSplitScroll = layout === "split-scroll";
  const hasStructuredContent =
    hasRenderableNode(icon) ||
    hasRenderableNode(title) ||
    hasRenderableNode(description) ||
    hasRenderableNode(actions);

  return (
    <tr
      {...emptyProps}
      ref={ref}
      data-slot="empty-row"
      className={cn(
        theme.slots.tableEmptyRow,
        isSplitScroll && theme.slots.tableSplitRow,
        classNames?.row,
        className,
      )}
      style={{
        ...theme.styles.tableEmpty,
        ...(isSplitScroll ? theme.styles.tableSplitRow : undefined),
        ...style,
      }}
    >
      <td
        colSpan={colSpan}
        data-slot="empty-cell"
        className={cn(
          theme.slots.tableEmptyCell,
          isSplitScroll && theme.slots.tableSplitCell,
          classNames?.cell,
        )}
        style={isSplitScroll ? { gridColumn: "1 / -1" } : undefined}
      >
        {hasStructuredContent ? (
          <div
            className={cn(theme.slots.tableEmptyContent, classNames?.content)}
          >
            {hasRenderableNode(icon) ? (
              <div className={cn(theme.slots.tableEmptyIcon, classNames?.icon)}>
                {icon}
              </div>
            ) : null}
            {hasRenderableNode(title) ? (
              <div
                className={cn(theme.slots.tableEmptyTitle, classNames?.title)}
              >
                {title}
              </div>
            ) : null}
            {hasRenderableNode(description) ? (
              <div
                className={cn(
                  theme.slots.tableEmptyDescription,
                  classNames?.description,
                )}
              >
                {description}
              </div>
            ) : null}
            {hasRenderableNode(actions) ? (
              <div
                className={cn(
                  theme.slots.tableEmptyActions,
                  classNames?.actions,
                )}
              >
                {actions}
              </div>
            ) : null}
          </div>
        ) : (
          children
        )}
      </td>
    </tr>
  );
});

function BaseTableLoadingSkeleton({
  baseClassName,
  className,
  style,
}: {
  baseClassName?: string;
  className?: string;
  style?: CSSProperties;
}) {
  const motion = useSkeletonMotion();
  return (
    <div
      ref={motion.ref}
      aria-hidden="true"
      data-slot="loading-skeleton"
      data-skeleton-animation={motion.mode}
      className={cn(
        baseClassName,
        motion.mode === 'pulse' && 'animate-pulse motion-reduce:animate-none',
        className,
      )}
      style={{ ...motion.style, ...style }}
    />
  );
}

export function BaseTableLoading({
  rows = 3,
  columns,
  columnWidths = [],
  animated = true,
  animation,
  rowLabel,
  appearance,
  className,
  classNames,
  style,
}: BaseTableLoadingProps) {
  if (!Number.isSafeInteger(rows) || rows < 0) {
    throw new Error('BaseTable loading rows must be a non-negative safe integer.');
  }
  if (!Number.isSafeInteger(columns) || columns < 1) {
    throw new Error('BaseTable loading columns must be a positive safe integer.');
  }
  if (!Array.isArray(columnWidths)) {
    throw new Error("BaseTable loading columnWidths must be an array.");
  }
  columnWidths.forEach((value, index) =>
    validateCssSizeValue(value, `loading columnWidths[${index}]`, {
      stringAllowed: true,
    }),
  );
  assertBoolean("loading animated", animated);
  assertOptionalLabel("loading rowLabel", rowLabel);
  const { layout, theme } = useBaseTableContext(appearance);
  const messages = useMonkeysLocaleMessages().table;
  const resolvedRowLabel = rowLabel ?? messages.loadingRow;
  const isSplitScroll = layout === "split-scroll";

  return (
    <SkeletonScope animation={animation} animated={animated}>
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <SkeletonItem key={rowIndex} index={rowIndex}>
          <tr
            data-slot="loading-row"
            aria-label={`${resolvedRowLabel} ${rowIndex + 1}`}
            aria-busy="true"
            className={cn(
              theme.slots.tableLoadingRow,
              isSplitScroll && theme.slots.tableSplitRow,
              classNames?.row,
              className,
            )}
            style={{
              ...theme.styles.tableLoading,
              ...(isSplitScroll ? theme.styles.tableSplitRow : undefined),
              ...style,
            }}
          >
            {Array.from({ length: columns }).map((__, columnIndex) => (
              <td
                key={columnIndex}
                data-slot="loading-cell"
                className={cn(
                  theme.slots.tableLoadingCell,
                  isSplitScroll && theme.slots.tableSplitCell,
                  classNames?.cell,
                )}
              >
                <BaseTableLoadingSkeleton
                  baseClassName={theme.slots.tableLoadingSkeleton}
                  className={classNames?.skeleton}
                  style={{
                    width: toCssSize(columnWidths[columnIndex]),
                  }}
                />
              </td>
            ))}
          </tr>
        </SkeletonItem>
      ))}
    </SkeletonScope>
  );
}
